import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import useMobxStores from 'hooks/useMobxStores';

type StartInput = string | number | Date | undefined | null;

export default function useIsClassOpen(
  openMinutes: number = 60,
  pollMs: number = 30_000,
): boolean {
  const [isOpen, setIsOpen] = useState(false);
  const { liveClassStore } = useMobxStores();

  const { startTime, tz } = useMemo(() => {
    if (liveClassStore?.offeringEnrolledLiveClass?.length) {
      const offeringEnrolledLiveClass =
        liveClassStore?.offeringEnrolledLiveClass?.[0] ?? {};

      const classStartTime: StartInput =
        offeringEnrolledLiveClass?.early_access_start_time ?? null;
      const classTz: string = offeringEnrolledLiveClass?.timezone ?? 'UTC';

      return { startTime: classStartTime, tz: classTz };
    }

    return { startTime: null, tz: 'UTC' };
  }, [liveClassStore?.offeringEnrolledLiveClass]);

  useEffect(() => {
    if (!startTime) {
      setIsOpen(false);
      return;
    }

    const start = DateTime.fromISO(String(startTime), { zone: tz });
    const openAt = start.minus({ minutes: openMinutes });

    const check = () => {
      const now = DateTime.now().setZone(tz);
      setIsOpen(now >= openAt);
    };

    check();

    // Poll so it flips without a reload
    const id = setInterval(check, pollMs);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(id);
  }, [startTime, tz, openMinutes, pollMs]);

  return isOpen;
}
